import styledComponents from "styled-components"
import React from "react"
import {Accordion, AccordionDetails, AccordionSummary, Typography} from "@mui/material"
import {ExpandMore as ExpandMoreIcon, Email as EmailIcon, GetApp as FileDownloadIcon} from "@mui/icons-material"

import {useNotification} from "@indebted/components/Notification"
import {api, CustomerEmailSentActivityPayload} from "@indebted/api"

function EmailSentActivity(
	panelId: number,
	{Title, HappenedAt, Payload}: {Title: string; HappenedAt: string; Payload: CustomerEmailSentActivityPayload},
	expanded: boolean,
	handleExpand: (panelID: number) => (event: any, isExpanded: boolean) => void,
	classes: {summary: string},
	customerID: string,
) {
	const activityID = Payload.ContentURI ? Payload.ContentURI.split("/")[2] : null
	const {notification} = useNotification()

	const handleDownloadPDF = async () => {
		try {
			if (activityID == null) {
				return
			}

			const pdfDownloadUrl = api.customer.downloadEmailPDFURL(customerID, activityID)

			const res = await fetch(pdfDownloadUrl, {
				headers: {Accept: "application/pdf"},
			})

			if (!res.ok) {
				console.error("Failed to fetch PDF:", res.status, res.statusText)
				notification.error("Failed to download PDF. Please try again or contact support if the issue persists.")
				return
			}

			const blob = await res.blob()
			const urlObject = URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = urlObject
			link.download = `email-${activityID}.pdf`
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(urlObject)
		} catch {
			notification.error("Failed to download PDF. Please try again or contact support if the issue persists.")
		}
	}

	return (
		<Accordion expanded={expanded} onChange={handleExpand(panelId)}>
			<StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
				<EmailIcon color="primary" />
				<Typography className={classes.summary}>
					{" "}
					<b>{Title}</b> - {HappenedAt} {Payload.Summary}
				</Typography>
			</StyledAccordionSummary>
			<AccordionDetails>
				{expanded && (
					<StyledDetailsContainer>
						<StyledHeaderRow>
							<Typography variant="body1" gutterBottom>
								<b>Sent To:</b> {Payload.Recipient}
							</Typography>
							{customerID && activityID && (
								<StyledDownloadButton onClick={handleDownloadPDF}>
									<FileDownloadIcon color="primary" />
									<Typography variant="body2" component="span" style={{marginLeft: "4px"}}>
										Download PDF
									</Typography>
								</StyledDownloadButton>
							)}
						</StyledHeaderRow>
						<StyledIFrame allowFullScreen src={api.url(Payload.ContentURI)} />
					</StyledDetailsContainer>
				)}
			</AccordionDetails>
		</Accordion>
	)
}

const StyledDetailsContainer = styledComponents.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const StyledHeaderRow = styledComponents.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
`

const StyledIFrame = styledComponents.iframe`
	width: 100%;
	flex-grow: 1;
	height: 600px;
	border: none;
`

const StyledAccordionSummary = styledComponents(AccordionSummary)`
	.MuiAccordionSummary-content {
		position: relative;
	}
`

const StyledDownloadButton = styledComponents.button`
	display: flex;
	align-items: center;
	border: none;
	background: none;
	cursor: pointer;
	padding: 8px 12px;
	border-radius: 4px;
	color: #1976d2;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: rgba(25, 118, 210, 0.08);
	}

	&:focus {
		outline: 2px solid #1976d2;
		outline-offset: 2px;
	}
`

export {EmailSentActivity}
